import { Target, TrendingUp, Users, MapPin, CheckCircle, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const marketSegments = [
  { segment: "Local Residents", percentage: 45, description: "Daytona Beach area residents seeking regular wellness care" },
  { segment: "Tourists & Visitors", percentage: 30, description: "Vacationers and seasonal visitors seeking relaxation" },
  { segment: "Corporate Wellness", percentage: 15, description: "Business clients and corporate wellness programs" },
  { segment: "Athletes & Sports", percentage: 10, description: "Athletes and fitness enthusiasts for recovery" },
];

const competitiveAdvantages = [
  "16 years of professional experience and expertise",
  "Prime location in thriving Daytona Beach tourism market",
  "Personalized treatment plans for each client",
  "Multiple massage modalities and certifications",
  "Strong reputation built on client testimonials",
  "Focus on preventative care and long-term wellness"
];

const marketOpportunities = [
  { title: "Growing Wellness Industry", stat: "$4.4T", description: "Global wellness economy continues rapid growth" },
  { title: "Tourism Market", stat: "10M+", description: "Annual visitors to Daytona Beach area" },
  { title: "Alternative Medicine Demand", stat: "38%", description: "Adults using complementary health approaches" },
  { title: "Repeat Client Rate", stat: "75%", description: "Industry average for massage therapy retention" },
];

export const BusinessPlanPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="business-plan-page">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-[#D4AF37] font-medium tracking-widest uppercase mb-4">
              Business Plan
            </p>
            <h1 className="section-heading text-5xl md:text-6xl font-bold mb-6">
              Strategic Growth Plan
            </h1>
            <p className="section-subheading mb-8">
              A comprehensive business plan for Millennium Wellness Spa, outlining our 
              mission, market opportunity, competitive positioning, and growth strategy 
              in the Daytona Beach wellness market.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/financials">
                <Button className="btn-secondary" data-testid="bp-financials-btn">
                  View Financial Projections
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <Link to="/dashboard">
                <Button variant="outline" className="h-12 px-8" data-testid="bp-dashboard-btn">
                  Grant Dashboard
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white" data-testid="mission-vision">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="p-8 bg-[#0F172A] text-white">
              <Target size={32} className="text-[#D4AF37] mb-4" />
              <h2 className="text-2xl font-semibold mb-4">Our Mission</h2>
              <p className="text-slate-300 leading-relaxed">
                To provide exceptional preventative care through professional massage therapy, 
                helping our clients achieve optimal wellness and quality of life. We are committed 
                to delivering personalized, therapeutic services that address individual needs 
                while promoting overall health and well-being in the Daytona Beach community.
              </p>
            </div>
            <div className="p-8 bg-slate-50 border border-slate-100">
              <TrendingUp size={32} className="text-[#D4AF37] mb-4" />
              <h2 className="text-2xl font-semibold text-[#0F172A] mb-4">Our Vision</h2>
              <p className="text-slate-600 leading-relaxed">
                To become Daytona Beach's premier destination for therapeutic massage and 
                alternative wellness services, recognized for excellence in client care 
                and community health promotion. We envision expanding our impact through 
                corporate partnerships, wellness programs, and continued investment in 
                practitioner expertise.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Market Opportunity */}
      <section className="py-20 bg-slate-50" data-testid="market-opportunity">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-[#D4AF37] font-medium tracking-widest uppercase mb-4">
              Market Analysis
            </p>
            <h2 className="section-heading text-4xl font-semibold mb-6">
              The Opportunity
            </h2>
            <p className="section-subheading">
              Daytona Beach presents a unique opportunity combining a thriving tourism 
              market with growing local demand for wellness services.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {marketOpportunities.map((item, index) => (
              <div 
                key={item.title}
                className="stat-card animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`market-stat-${index}`}
              >
                <p className="text-3xl font-bold text-[#D4AF37] mb-2">{item.stat}</p>
                <h3 className="font-semibold text-[#0F172A] mb-1">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>

          {/* Market Segments */}
          <div className="bg-white p-8 border border-slate-100">
            <h3 className="text-xl font-semibold text-[#0F172A] mb-6 flex items-center gap-2">
              <Users size={24} className="text-[#D4AF37]" />
              Target Market Segments
            </h3>
            <div className="space-y-6">
              {marketSegments.map((segment, index) => (
                <div key={segment.segment} data-testid={`market-segment-${index}`}>
                  <div className="flex justify-between mb-2">
                    <span className="font-medium text-[#0F172A]">{segment.segment}</span>
                    <span className="text-[#D4AF37] font-semibold">{segment.percentage}%</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-[#D4AF37] transition-all duration-1000"
                      style={{ width: `${segment.percentage}%` }}
                    />
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{segment.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Competitive Advantages */}
      <section className="py-20 bg-white" data-testid="competitive-advantages">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#D4AF37] font-medium tracking-widest uppercase mb-4">
                Why Choose Us
              </p>
              <h2 className="section-heading text-4xl font-semibold mb-6">
                Competitive Advantages
              </h2>
              <p className="section-subheading mb-8">
                Millennium Wellness Spa is uniquely positioned to capture market share 
                through our combination of experience, location, and service quality.
              </p>
              
              <div className="space-y-4">
                {competitiveAdvantages.map((advantage, index) => (
                  <div 
                    key={advantage}
                    className="flex items-start gap-3 animate-fade-in"
                    style={{ animationDelay: `${index * 100}ms` }}
                    data-testid={`advantage-${index}`}
                  >
                    <CheckCircle size={20} className="text-[#D4AF37] mt-1 flex-shrink-0" />
                    <span className="text-slate-700">{advantage}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#0F172A] p-8">
              <div className="flex items-center gap-3 mb-6">
                <MapPin size={24} className="text-[#D4AF37]" />
                <h3 className="text-xl font-semibold text-white">Location Advantage</h3>
              </div>
              <p className="text-slate-300 leading-relaxed mb-6">
                Daytona Beach, FL is a premier tourist destination with over 10 million 
                visitors annually. This prime location provides access to:
              </p>
              <ul className="space-y-3 text-slate-300">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-[#D4AF37] rounded-full" />
                  Year-round tourist traffic
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-[#D4AF37] rounded-full" />
                  Growing local population
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-[#D4AF37] rounded-full" />
                  Strong hospitality partnerships
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-[#D4AF37] rounded-full" />
                  Limited high-quality competition
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Ask */}
      <section className="py-20 bg-[#0F172A]" data-testid="investment-ask">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <p className="text-[#D4AF37] font-medium tracking-widest uppercase mb-4">
            Investment Opportunity
          </p>
          <h2 className="text-4xl font-semibold text-white mb-6 font-display">
            Partner with Millennium Wellness Spa
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto mb-8">
            We are seeking strategic investment to expand our services, enhance our 
            facility, and capture a larger share of the Daytona Beach wellness market. 
            Review our financial projections to see the growth potential.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/financials">
              <Button className="btn-secondary h-12 px-8" data-testid="investment-financials-btn">
                View Financial Projections
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="h-12 px-8 border-white text-white hover:bg-white/10" data-testid="investment-contact-btn">
                Contact for Investment
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BusinessPlanPage;
